import { TemplateWithFieldsContext } from "@/contexts/templatesWithFields";
import { type TemplatesWithFields, templatesWithFieldsQuery } from "@/integrations/supabase/templateEditor";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";

type Template = TemplatesWithFields[number];

export interface TemplateContextType {
  template: Template | undefined;
  isLoading: boolean;
  isPending: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  getQueryData: () => Template | undefined;
  setQueryData: (updater: Template | ((oldData: Template) => Template)) => void;
  getQueryKey: () => string[];
  invalidateQuery: () => Promise<void>;
}

interface TemplateProviderProps {
  children: ReactNode;
  templateId: string;
}

export const TemplateWithFieldsProvider = ({ children, templateId }: TemplateProviderProps) => {
  const queryClient = useQueryClient();
  const {
    data: template,
    isLoading,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["template", templateId],
    queryFn: async () => {
      const { data, error } = await templatesWithFieldsQuery.eq("id", templateId).single();

      if (error) {
        console.error("Error fetching template:", error);
        toast.error("Failed to load template");
        throw error;
      }

      return data;
    },
    enabled: !!templateId,
  });

  const value = {
    template,
    isLoading,
    isPending,
    error: error as Error | null,
    refetch: async () => {
      await refetch();
    },
    getQueryData: () => {
      return queryClient.getQueryData<Template>(["template", templateId]);
    },
    getQueryKey: () => {
      return ["template", templateId];
    },
    setQueryData: (updater: Template | ((oldData: Template) => Template)) => {
      queryClient.setQueryData(["template", templateId], updater);
    },
    invalidateQuery: () => {
      return queryClient.invalidateQueries({
        queryKey: ["template", templateId],
      });
    },
  };

  if (isPending) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <TemplateWithFieldsContext.Provider value={value}>{children}</TemplateWithFieldsContext.Provider>;
};
